/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtomValue,
  useStateXGetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import zIndex from 'constants/ZIndex';
import useWindowSize from 'hooks/useWindowSize';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { memo, useContext, useEffect, useState } from 'react';
import Portal from 'utils/Portal';
import { componentTipAtom, sidebarLocationOptionAtom } from 'utils/state';
import { Theme } from '@mui/material';
import { draggingAtom, hoverItemsAtom } from './atoms';
import { activeItemSelector } from './activeItemSelector';
import useUser from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: zIndex.ComponentTip,
    boxSizing: 'border-box',
  },
  topLine: {
    borderBottom: `${theme.palette.secondary.light} dashed 1px`,
  },
  rightLine: {
    borderLeft: `${theme.palette.secondary.light} dashed 1px`,
  },
  bottomLine: {
    borderTop: `${theme.palette.secondary.light} dashed 1px`,
  },
  leftLine: {
    borderRight: `${theme.palette.secondary.light} dashed 1px`,
  },
  icon: {
    cursor: 'pointer',
    marginLeft: '12px',
  },
  name: {
    pointerEvents: 'none',
    boxSizing: 'border-box',
    position: 'fixed',
    zIndex: zIndex.ComponentTip,
    backgroundColor: theme.palette.secondary.light,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomWidth: 0,
    padding: '0 16px',
    color: theme.palette.secondary.contrastText,
    fontWeight: 'bold',
    height: '30px',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    '& > span': {
      marginRight: 16,
    },
  },
  hover: {},
}));

export const RefCache: Record<
  string,
  React.MutableRefObject<HTMLElement | null>
> = {};

function ComponentHoverTip({
  appUid,
  pageId,
}: {
  appUid: string;
  pageId: string;
}) {
  const classes = useStyles();
  const dragging = useStateXAtomValue(draggingAtom);
  const user = useUser();
  const showComponentTip = useStateXAtomValue(componentTipAtom, {
    params: { userName: user.userName },
  });
  const sidebarRight = useStateXAtomValue(sidebarLocationOptionAtom);
  const [left, setLeft] = useState(0);
  const [width, setWidth] = useState(0);
  const [top, setTop] = useState(0);
  const [height, setHeight] = useState(0);
  const platform = useContext(PlatformContext);
  const hoverItems = useStateXAtomValue(hoverItemsAtom);
  const { itemId } = useStateXSelector(activeItemSelector, {
    params: { appUid, treename: 'designer', pageId },
    props: platform,
  });
  const get = useStateXGetter();

  const hoverItem = hoverItems.length
    ? hoverItems[hoverItems.length - 1]
    : null;

  const mgr = platform.getManager(hoverItem ? hoverItem.itemId : '');

  const [innerWidth, innerHeight] = useWindowSize();
  useEffect(() => {
    const ref =
      RefCache[`${pageId}-${hoverItem?.itemId ?? 'x'}-${hoverItem?.rowIndex}`];
    if (hoverItem && ref?.current) {
      const rect = ref.current.getBoundingClientRect();
      setLeft(rect.left);
      setWidth(rect.width);
      setTop(rect.top);
      setHeight(rect.height);
    } else {
      setLeft(0);
      setWidth(0);
      setTop(0);
      setHeight(0);
    }
  }, [get, hoverItem, itemId, pageId]);

  if (
    !showComponentTip ||
    dragging ||
    !hoverItem ||
    !mgr ||
    itemId === hoverItem.itemId ||
    width === 0 ||
    height === 0
  ) {
    return null;
  }

  const pageLeft = sidebarRight ? 0 : 40;
  const pageRight = sidebarRight ? innerWidth - 40 : innerWidth;
  const pageBottom = innerHeight;

  const nameStyle = {
    top: top - 32,
    left: Math.max(left - 4, pageLeft),
  };
  if (nameStyle.top < 0) {
    nameStyle.top = top + height - 22 + 4;
  }
  if (nameStyle.top === 38) {
    nameStyle.top = 40;
  }
  const topStyle = {
    width: Math.min(width + 4, pageRight),
    height: 2,
    top: top - 4,
    left: Math.max(left - 4, pageLeft),
  };
  const rightStyle = {
    width: 2,
    height: height + 4,
    top: top - 2,
    left: left + width + 2,
  };
  if (rightStyle.left > pageRight - 2 && rightStyle.left <= pageRight + 2) {
    rightStyle.left = pageRight - 2;
  }
  const bottomStyle = {
    width: Math.min(width + 4, pageRight),
    height: 2,
    top: top + height + 2,
    left: Math.max(left - 4, pageLeft),
  };
  if (bottomStyle.top > pageBottom - 2 && bottomStyle.top <= pageBottom + 2) {
    bottomStyle.top = pageBottom - 2;
  }
  const leftStyle = {
    width: 2,
    height: height + 4,
    top: top - 2,
    left: Math.max(left - 4, pageLeft),
  };

  return (
    <Portal>
      <div
        key="ct-n"
        className={clsx(classes.name, classes.hover)}
        style={nameStyle}
      >
        <span>{mgr.name}</span>
      </div>
      <div
        key="ct-t"
        className={clsx(classes.root, classes.topLine, classes.hover)}
        style={topStyle}
      />
      <div
        key="ct-r"
        className={clsx(classes.root, classes.rightLine, classes.hover)}
        style={rightStyle}
      />
      <div
        key="ct-b"
        className={clsx(classes.root, classes.bottomLine, classes.hover)}
        style={bottomStyle}
      />
      <div
        key="ct-l"
        className={clsx(classes.root, classes.leftLine, classes.hover)}
        style={leftStyle}
      />
    </Portal>
  );
}

export default memo(ComponentHoverTip);
